import { useQueryHelper } from '@anytag/shared-hooks';
import { useYoutubeAnalyticsRelatedPostsQuery } from '@anytag/graphql';
import { bigIntFormatter, formatDate, getYoutubeVideoUrl } from '@anytag/shared-utils';
import { Anchor, TextCutter, MediaPreview, BaseModal, BaseModalProps, CircularProgress } from '@anytag/shared-atoms';

export interface YoutubeAnalyticsRelatedPostsModalProps extends Omit<BaseModalProps, 'open' | 'footer' | 'children'> {
  tag?: string | null;
  postId?: number | null;
  channelId?: number | null;
  channelName?: string | null;
}

export const YoutubeAnalyticsRelatedPostsModal = ({
  tag,
  postId,
  onClose,
  channelId,
  channelName,
  ...modalProps
}: YoutubeAnalyticsRelatedPostsModalProps) => {
  const { t, enqueueSnackbar } = useQueryHelper();

  const hasData = !!(tag && postId && channelId);
  const { data, loading } = useYoutubeAnalyticsRelatedPostsQuery({
    skip: !hasData,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    variables: {
      tag: String(tag),
      youtubeAnalyticsPostId: postId as number,
      youtubeAnalyticsAccountId: channelId as number
    },
    onCompleted: ({ youtubeAnalyticsRelatedPosts }) => {
      if (!youtubeAnalyticsRelatedPosts.posts.length) {
        enqueueSnackbar(t('There are no related videos found'), { variant: 'info' });
        onClose?.();
      }
    },
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
      onClose?.();
    }
  });

  const relatedPosts = data?.youtubeAnalyticsRelatedPosts.posts || [];

  return (
    <BaseModal
      size="lg"
      open={hasData}
      onClose={onClose}
      title={`#${tag} ${t('Related Videos')}`}
      footer={{ cancelButton: { onClick: onClose } }}
      {...modalProps}
    >
      <div className="min-h-100">
        {loading ? (
          <div className="flex size-full min-h-[inherit] items-center justify-center">
            <CircularProgress size={64} thickness={1} className="bg-black-30" />
          </div>
        ) : (
          <div className="min-h-[inherit]">
            {relatedPosts.map(
              ({ id, views, comments, postDate, videoId, videoTitle, videoCaption, videoThumbnail }) => (
                <div key={id} className="flex not-first-of-type:mt-4">
                  <MediaPreview src={videoThumbnail} objectFit="cover" className="h-26 w-45" />

                  <div className="ml-4">
                    <Anchor target="_blank" href={getYoutubeVideoUrl(videoId)} label={videoTitle} />
                    <div className="mt-1 flex items-center">
                      {channelName ? (
                        <>
                          <p>{channelName}</p>
                          <p className="mx-1.5">/</p>
                        </>
                      ) : null}

                      <p className="mr-1 text-sm font-semibold">{bigIntFormatter(views, 0)}</p>
                      <p className="text-base lowercase">{t('General.Views')}</p>

                      <p className="mx-1.5">/</p>
                      <p className="mr-1 text-sm font-semibold">{bigIntFormatter(comments, 0)}</p>
                      <p className="text-base lowercase">{t('General.Comments')}</p>

                      <p className="mx-1.5">/</p>
                      <p className="text-base">{formatDate(postDate)}</p>
                    </div>
                    <TextCutter lines={3} text={videoCaption} className="text-sm text-black-60" />
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </BaseModal>
  );
};
